import type { JSX } from "react";
import { useMemo } from "react";
import { axisTicks } from "./timeAxis";
import { niceCeil, Y_TICK_FRACTIONS } from "./geometry";

export interface StackedSegment { value: number; color: string; }
export interface StackedBucket { ts: number; segments: StackedSegment[]; }
export interface LegendItem { label: string; color: string; }

/**
 * Stacked time-bucket bar chart with a y-axis (value scale), an x-axis (time,
 * when from/to are given) and a colour legend. Each segment carries its own
 * `color` (a token like var(--cc-cat-1) or a semantic var); the `legend` maps
 * those colours to labels so every colour on the chart is explained. Y labels
 * are an HTML overlay so they render crisply alongside the flex-based bars.
 */
export function StackedBar({ buckets, legend, from, to, height = 120, formatY }: {
  buckets: StackedBucket[];
  /** Colour→label legend. Omit when the caller renders its own richer legend. */
  legend?: LegendItem[];
  from?: number;
  to?: number;
  height?: number;
  /** Format a y-axis value in its unit (count / tokens / …). Default: raw number. */
  formatY?: (n: number) => string;
}): JSX.Element {
  const fmtY = formatY ?? ((n: number) => String(Math.round(n)));
  const hasAxis = from !== undefined && to !== undefined && to > from;
  // Derive the scale + axis ticks once per (buckets, window) change — this chart
  // renders in a grid that re-renders on every live tick, so recomputing the
  // per-bucket totals each time would be wasted work.
  const { ceil, yTicks, ticks } = useMemo(() => {
    const totals = buckets.map((b) => b.segments.reduce((s, x) => s + x.value, 0));
    const c = niceCeil(Math.max(1, ...totals));
    return {
      ceil: c,
      yTicks: Y_TICK_FRACTIONS.map((f) => f * c),
      ticks: hasAxis ? axisTicks(from, to) : []
    };
  }, [buckets, from, to, hasAxis]);
  const tickPct = (ts: number): number => hasAxis ? ((ts - from) / (to - from)) * 100 : 0;

  return (
    <div className="cc-chart">
      <div className="cc-chart-main">
        <div className="cc-yaxis" style={{ height }} aria-hidden="true">
          {yTicks.map((v) => (
            <span key={v} className="cc-yaxis-label" style={{ top: `${(1 - v / ceil) * 100}%` }}>{fmtY(v)}</span>
          ))}
        </div>
        <div className="cc-plot">
          <div className="cc-stacked" style={{ height }}>
            {buckets.map((b) => (
              <div key={b.ts} className="cc-stacked-col" title={new Date(b.ts).toLocaleString()}>
                {b.segments.map((seg, j) => (
                  <span key={j} className="cc-stacked-seg" style={{ height: `${(seg.value / ceil) * 100}%`, background: seg.color }} />
                ))}
              </div>
            ))}
          </div>
          {hasAxis && (
            <div className="cc-axis-row" aria-hidden="true">
              {ticks.map((t) => (
                <span key={t.ts} className="cc-axis-label" style={{ left: `${tickPct(t.ts)}%` }}>{t.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {legend !== undefined && (
        <div className="cc-legend">
          {legend.map((it) => (
            <span key={it.label} className="cc-legend-item">
              <span className="cc-legend-swatch" style={{ background: it.color }} aria-hidden="true" />
              {it.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
